import { useState, useEffect } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { Inbox, TriangleAlert, ChartColumnBig, Gauge } from 'lucide-react';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import TicketService from '@Helpers/URLHelpers/KaiziqResolve/TicketService';
import KaiziqResolveConnectionService from '@Helpers/URLHelpers/KaiziqResolveConnectionService';
import KaiziqResolveConnectionAnalyticsService from '@Helpers/URLHelpers/KaiziqResolve/views/KaiziqResolveConnectionAnalyticsService';
import RegistryLinkViewService from '@Helpers/URLHelpers/WeboutService/views/RegistryLinkViewService';
import LabelRegistryService from '@Helpers/URLHelpers/WeboutService/views/LabelRegistryService';
import AssignedTicketsPanel from '@IssueConsoleRepresentationalDisplay/AssignedTicketsPanel/AssignedTicketsPanel';
import EscalatedStaleTicketsPanel from '@IssueConsoleRepresentationalDisplay/EscalatedStaleTicketsPanel/EscalatedStaleTicketsPanel';
import FormsAnalyticsPanel from '@IssueConsoleRepresentationalDisplay/FormsAnalyticsPanel/FormsAnalyticsPanel';
import UserAnalyticsPanel from '@IssueConsoleRepresentationalDisplay/UserAnalyticsPanel/UserAnalyticsPanel';
import TicketFilterBar from '@IssueConsoleRepresentationalDisplay/TicketFilterBar/TicketFilterBar';
import DashboardPagination from '@IssueConsoleRepresentationalDisplay/DashboardPagination/DashboardPagination';
import TicketView from '@IssueConsoleViews/TicketView';
import '@Partials/_SimpleStyles.scss';
import '@Partials/_ComStyles.scss';
import './DashboardPage.scss';

const STALE_DAYS = 5;
const PAGE_SIZE = 10;
const FORMS_PAGE_SIZE = 4;

export default function DashboardPage() {
    const location = useLocation();
    const [searchParams] = useSearchParams();

    // Legacy deep links pointed at /dashboard when it rendered IssueOverviewPage.
    // Only /forms knows how to auto-open a ticket from these signals, so hand them over.
    const isLegacyTicketDeepLink =
        searchParams.has('ticketId') || searchParams.has('formId') || Boolean(location.state?.deepLink);

    const { currentProjectId, getSimpleUsersByProjectId } = useReduxProject();

    const [assignedTickets, setAssignedTickets] = useState([]);
    const [assignedTicketLabels, setAssignedTicketLabels] = useState(new Map());
    const [assignedLoading, setAssignedLoading] = useState(true);
    const [assignedError, setAssignedError] = useState(false);
    const [assignedPage, setAssignedPage] = useState(0);
    const [assignedTotalCount, setAssignedTotalCount] = useState(0);
    const [assignedStatusFilter, setAssignedStatusFilter] = useState(null);
    const [assignedLabelFilter, setAssignedLabelFilter] = useState([]);
    const [assignedSortBy, setAssignedSortBy] = useState('created_desc');

    const [escalatedStaleTickets, setEscalatedStaleTickets] = useState([]);
    const [escalatedStaleLoading, setEscalatedStaleLoading] = useState(true);
    const [escalatedStaleError, setEscalatedStaleError] = useState(false);
    const [escalatedPage, setEscalatedPage] = useState(0);
    const [escalatedTotalCount, setEscalatedTotalCount] = useState(0);
    const [escalatedStatusFilter, setEscalatedStatusFilter] = useState(null);
    const [escalatedLabelFilter, setEscalatedLabelFilter] = useState([]);
    const [escalatedAssigneeFilter, setEscalatedAssigneeFilter] = useState(null);
    const [escalatedSortBy, setEscalatedSortBy] = useState('modified_asc');

    const [labelOptions, setLabelOptions] = useState([]);
    const [assigneeOptions, setAssigneeOptions] = useState([]);

    const [forms, setForms] = useState([]);
    const [formsLoading, setFormsLoading] = useState(true);
    const [formsError, setFormsError] = useState(false);
    const [formsPage, setFormsPage] = useState(0);

    const [userAnalytics, setUserAnalytics] = useState(null);
    const [userAnalyticsLoading, setUserAnalyticsLoading] = useState(true);
    const [userAnalyticsError, setUserAnalyticsError] = useState(false);

    const [selectedTicketId, setSelectedTicketId] = useState(null);
    const [selectedFormId, setSelectedFormId] = useState(null);

    const className = 'dashboardPage';

    const ticketService = new TicketService();
    const kaiziqResolveConnectionService = new KaiziqResolveConnectionService();
    const kaiziqResolveConnectionAnalyticsService = new KaiziqResolveConnectionAnalyticsService();
    const registryLinkViewService = new RegistryLinkViewService();
    const labelRegistryService = new LabelRegistryService();

    useEffect(() => {
        if (isLegacyTicketDeepLink) return;
        if (!currentProjectId) return;
        loadForms();
        loadUserAnalytics();
        loadAssigneeOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentProjectId, isLegacyTicketDeepLink]);

    useEffect(() => {
        if (isLegacyTicketDeepLink) return;
        if (!currentProjectId) return;
        loadAssignedTickets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentProjectId, isLegacyTicketDeepLink, assignedPage, assignedStatusFilter, assignedLabelFilter, assignedSortBy]);

    useEffect(() => {
        if (isLegacyTicketDeepLink) return;
        if (!currentProjectId) return;
        loadEscalatedStaleTickets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        currentProjectId,
        isLegacyTicketDeepLink,
        escalatedPage,
        escalatedStatusFilter,
        escalatedLabelFilter,
        escalatedAssigneeFilter,
        escalatedSortBy,
    ]);

    if (isLegacyTicketDeepLink) {
        return <Navigate to={'/forms' + location.search} state={location.state} replace />;
    }

    const loadAssignedTickets = async () => {
        setAssignedLoading(true);
        setAssignedError(false);
        try {
            const [ticketsResponse, linksResponse, labelsResponse] = await Promise.all([
                ticketService.getDashboardAssignedTickets(currentProjectId, {
                    page: assignedPage,
                    pageSize: PAGE_SIZE,
                    statusValue: assignedStatusFilter,
                    labelIds: assignedLabelFilter,
                    sortBy: assignedSortBy,
                }),
                registryLinkViewService.getListByProject(currentProjectId),
                labelRegistryService.getListByProject(currentProjectId),
            ]);

            const paged = JSON.parse(ticketsResponse.body);
            const tickets = Array.isArray(paged.items) ? paged.items : [];

            // Labels live in a separate service (registry links + label registry) with no
            // per-ticket-list endpoint, so this joins the project's links and label
            // definitions client-side rather than firing one label fetch per ticket row. The
            // full label definition list doubles as the filter dropdown's option set.
            //
            // "Label" here covers three registry types — labelCard, nameCard, and orgCard — any
            // of the three can be attached to a ticket as a label. Their ids come from separate
            // sequences and collide with each other, so everything is keyed by a composite
            // "type_id" token rather than the bare numeric id.
            const LABEL_TYPES = ['labelCard', 'nameCard', 'orgCard'];
            const labelKey = (type, id) => type + '_' + id;

            const links = JSON.parse(linksResponse.body) || [];
            const labelDefs = (JSON.parse(labelsResponse.body) || []).filter((l) => LABEL_TYPES.includes(l.type));
            const labelById = new Map(labelDefs.map((l) => [labelKey(l.type, l.id), l]));
            const labelsByTicketId = new Map();
            links
                .filter((l) => l.targetType === 'ticket' && LABEL_TYPES.includes(l.registryType))
                .forEach((l) => {
                    const label = labelById.get(labelKey(l.registryType, l.registryId));
                    if (!label) return; // dangling link (label deleted) — skip defensively
                    const ticketKey = Number(l.targetId);
                    const list = labelsByTicketId.get(ticketKey) || [];
                    list.push(label);
                    labelsByTicketId.set(ticketKey, list);
                });

            setAssignedTickets(tickets);
            setAssignedTicketLabels(labelsByTicketId);
            setAssignedTotalCount(paged.totalCount || 0);
            setLabelOptions(labelDefs.map((l) => ({ id: labelKey(l.type, l.id), name: l.name, type: l.type })));
        } catch (err) {
            setAssignedError(true);
        } finally {
            setAssignedLoading(false);
        }
    };

    const loadEscalatedStaleTickets = async () => {
        setEscalatedStaleLoading(true);
        setEscalatedStaleError(false);
        try {
            const response = await ticketService.getDashboardEscalatedStaleTickets(currentProjectId, STALE_DAYS, {
                page: escalatedPage,
                pageSize: PAGE_SIZE,
                statusValue: escalatedStatusFilter,
                assigneeUserId: escalatedAssigneeFilter,
                labelIds: escalatedLabelFilter,
                sortBy: escalatedSortBy,
            });
            const paged = JSON.parse(response.body);
            setEscalatedStaleTickets(Array.isArray(paged.items) ? paged.items : []);
            setEscalatedTotalCount(paged.totalCount || 0);
        } catch (err) {
            setEscalatedStaleError(true);
        } finally {
            setEscalatedStaleLoading(false);
        }
    };

    const loadForms = async () => {
        setFormsLoading(true);
        setFormsError(false);
        try {
            const [connectionResponse, analyticsResponse] = await Promise.all([
                kaiziqResolveConnectionService.getKaiziqResolveConnectionListByProject(currentProjectId),
                kaiziqResolveConnectionAnalyticsService.getListByProject(currentProjectId),
            ]);

            const connections = JSON.parse(connectionResponse.body);
            const analytics = JSON.parse(analyticsResponse.body);

            const statusBreakdownResponses = await Promise.all(
                connections.map((c) => ticketService.getAnalysisByForm(c.id))
            );
            const statusBreakdowns = statusBreakdownResponses.map((r) => JSON.parse(r.body));

            const merged = connections.map((c, index) => {
                const analyticsForForm = analytics.find((a) => a.formId === c.id) || {};
                const breakdown = statusBreakdowns[index] || {};
                return {
                    formId: c.id,
                    title: c.title,
                    statusList: breakdown.statusList || [],
                    reporters: analyticsForForm.reporters,
                    totalTickets: analyticsForForm.totalTickets,
                    averageTickets: analyticsForForm.averageTickets,
                };
            });

            setForms(merged);
            setFormsPage(0);
        } catch (err) {
            setFormsError(true);
        } finally {
            setFormsLoading(false);
        }
    };

    const loadUserAnalytics = async () => {
        setUserAnalyticsLoading(true);
        setUserAnalyticsError(false);
        try {
            const response = await ticketService.getDashboardUserAnalytics(currentProjectId);
            const data = JSON.parse(response.body);
            setUserAnalytics(data);
        } catch (err) {
            setUserAnalyticsError(true);
        } finally {
            setUserAnalyticsLoading(false);
        }
    };

    const loadAssigneeOptions = async () => {
        try {
            const users = await getSimpleUsersByProjectId(currentProjectId);
            const options = (users || [])
                .map((u) => ({ id: Number(u.id), label: u.fullName || u.userName }))
                .filter((u) => !!u.id);
            setAssigneeOptions(options);
        } catch (err) {
            // Non-critical: the assignee filter just has no options; the rest of the
            // dashboard is unaffected.
        }
    };

    const handleAssignedStatusChange = (value) => {
        setAssignedStatusFilter(value);
        setAssignedPage(0);
    };

    const handleAssignedLabelsChange = (values) => {
        setAssignedLabelFilter(values);
        setAssignedPage(0);
    };

    const handleAssignedSortChange = (value) => {
        setAssignedSortBy(value);
        setAssignedPage(0);
    };

    const handleEscalatedStatusChange = (value) => {
        setEscalatedStatusFilter(value);
        setEscalatedPage(0);
    };

    const handleEscalatedLabelsChange = (values) => {
        setEscalatedLabelFilter(values);
        setEscalatedPage(0);
    };

    const handleEscalatedAssigneeChange = (value) => {
        setEscalatedAssigneeFilter(value);
        setEscalatedPage(0);
    };

    const handleEscalatedSortChange = (value) => {
        setEscalatedSortBy(value);
        setEscalatedPage(0);
    };

    const openTicket = (ticket) => {
        setSelectedTicketId(ticket.id);
        setSelectedFormId(ticket.kaiziqResolveConnectionId);
    };

    const closeTicket = () => {
        setSelectedTicketId(null);
        setSelectedFormId(null);
    };

    return (
        <div className={className}>
            <div className={className + '__grid'}>
                <div className={className + '__section ' + className + '__section--full'}>
                    <div className={className + '__panel ' + className + '__panel--compact'}>
                        <div className={className + '__panel__header'}>
                            <div className={className + '__panel__header__icon'}>
                                <Gauge />
                            </div>
                            <div className={className + '__panel__header__title'}>
                                <span className={className + '__panel__header__title__text'}>Your Performance</span>
                            </div>
                        </div>
                        <div className={className + '__panel__body'}>
                            <UserAnalyticsPanel
                                analytics={userAnalytics}
                                loading={userAnalyticsLoading}
                                error={userAnalyticsError}
                                onRetry={loadUserAnalytics}
                            />
                        </div>
                    </div>
                </div>

                <div className={className + '__section ' + className + '__section--full'}>
                    <div className={className + '__panel ' + className + '__panel--primary'}>
                        <div className={className + '__panel__header'}>
                            <div className={className + '__panel__header__icon'}>
                                <Inbox />
                            </div>
                            <div className={className + '__panel__header__title'}>
                                <span className={className + '__panel__header__title__text'}>My Assigned Tickets</span>
                                {!assignedLoading && !assignedError && (
                                    <span className={className + '__panel__header__title__count'}>
                                        {assignedTotalCount}
                                    </span>
                                )}
                            </div>
                        </div>
                        <TicketFilterBar
                            statusValue={assignedStatusFilter}
                            onStatusChange={handleAssignedStatusChange}
                            labelOptions={labelOptions}
                            selectedLabelIds={assignedLabelFilter}
                            onLabelsChange={handleAssignedLabelsChange}
                            sortBy={assignedSortBy}
                            onSortChange={handleAssignedSortChange}
                        />
                        <div className={className + '__panel__body'}>
                            <AssignedTicketsPanel
                                tickets={assignedTickets}
                                labelsByTicketId={assignedTicketLabels}
                                loading={assignedLoading}
                                error={assignedError}
                                onRetry={loadAssignedTickets}
                                onTicketClick={openTicket}
                            />
                            {!assignedLoading && !assignedError && (
                                <DashboardPagination
                                    page={assignedPage}
                                    pageSize={PAGE_SIZE}
                                    totalCount={assignedTotalCount}
                                    onPageChange={setAssignedPage}
                                />
                            )}
                        </div>
                    </div>
                </div>

                <div className={className + '__section'}>
                    <div className={className + '__panel'}>
                        <div className={className + '__panel__header'}>
                            <div className={className + '__panel__header__icon'}>
                                <TriangleAlert />
                            </div>
                            <div className={className + '__panel__header__title'}>
                                <span className={className + '__panel__header__title__text'}>Escalated / Stale Tickets</span>
                                {!escalatedStaleLoading && !escalatedStaleError && (
                                    <span className={className + '__panel__header__title__count'}>
                                        {escalatedTotalCount}
                                    </span>
                                )}
                            </div>
                        </div>
                        <TicketFilterBar
                            statusValue={escalatedStatusFilter}
                            onStatusChange={handleEscalatedStatusChange}
                            labelOptions={labelOptions}
                            selectedLabelIds={escalatedLabelFilter}
                            onLabelsChange={handleEscalatedLabelsChange}
                            assigneeOptions={assigneeOptions}
                            selectedAssigneeId={escalatedAssigneeFilter}
                            onAssigneeChange={handleEscalatedAssigneeChange}
                            sortBy={escalatedSortBy}
                            onSortChange={handleEscalatedSortChange}
                        />
                        <div className={className + '__panel__body'}>
                            <EscalatedStaleTicketsPanel
                                tickets={escalatedStaleTickets}
                                loading={escalatedStaleLoading}
                                error={escalatedStaleError}
                                staleDays={STALE_DAYS}
                                onRetry={loadEscalatedStaleTickets}
                                onTicketClick={openTicket}
                            />
                            {!escalatedStaleLoading && !escalatedStaleError && (
                                <DashboardPagination
                                    page={escalatedPage}
                                    pageSize={PAGE_SIZE}
                                    totalCount={escalatedTotalCount}
                                    onPageChange={setEscalatedPage}
                                />
                            )}
                        </div>
                    </div>
                </div>

                <div className={className + '__section'}>
                    <div className={className + '__panel'}>
                        <div className={className + '__panel__header'}>
                            <div className={className + '__panel__header__icon'}>
                                <ChartColumnBig />
                            </div>
                            <div className={className + '__panel__header__title'}>
                                <span className={className + '__panel__header__title__text'}>Forms Analytics</span>
                                {!formsLoading && !formsError && (
                                    <span className={className + '__panel__header__title__count'}>
                                        {forms.length}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className={className + '__panel__body'}>
                            <FormsAnalyticsPanel
                                forms={forms.slice(formsPage * FORMS_PAGE_SIZE, (formsPage + 1) * FORMS_PAGE_SIZE)}
                                loading={formsLoading}
                                error={formsError}
                                onRetry={loadForms}
                            />
                            {!formsLoading && !formsError && (
                                <DashboardPagination
                                    page={formsPage}
                                    pageSize={FORMS_PAGE_SIZE}
                                    totalCount={forms.length}
                                    onPageChange={setFormsPage}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {selectedTicketId && (
                <TicketView
                    open={true}
                    closePopup={closeTicket}
                    ticketId={selectedTicketId}
                    formId={selectedFormId}
                    reloadData={() => {
                        loadAssignedTickets();
                        loadEscalatedStaleTickets();
                        loadUserAnalytics();
                    }}
                />
            )}
        </div>
    );
}
